import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { AuthService } from '@core/service/auth.service';
import { RestClientService } from '@core/service/rest-client.service';
import { UtilitiesService } from '@core/service/utilities.service';
import { SearchMdicamentProComponent } from '@shared/components/search-mdicament-pro/search-mdicament-pro.component';
import { FormPresOptiqueComponent } from '../../../prestation/prestation-main/main-optique/form-pres-optique/form-pres-optique.component';
import { FormPresDentisterieComponent } from '../../../prestation/prestation-main/main-dentisterie/form-pres-dentisterie/form-pres-dentisterie.component';
import Swal from 'sweetalert2';

interface GarantieCol {
  codif: string;
  label: string;
  icon: string;
  countKey: string;
  activiteCode: string;
  isOrdonnance?: boolean;
  hospitOnly?: boolean;
}

const ALL_COLS: GarantieCol[] = [
  { codif: 'PHARMA', label: 'Ordonnance',    icon: 'edit_note',          countKey: 'count_pharma_non_hospit', activiteCode: 'ORDONNANCE', isOrdonnance: true },
  { codif: 'BIO',    label: 'Biologie',      icon: 'biotech',            countKey: 'count_bio_non_hospit',   activiteCode: 'BIO' },
  { codif: 'IMA',    label: 'Imagerie',      icon: 'image_search',       countKey: 'count_ima_non_hospit',   activiteCode: 'IMA' },
  { codif: 'OPT',    label: 'Optique',       icon: 'visibility',         countKey: 'count_opt_non_hospit',   activiteCode: 'OPT' },
  { codif: 'DEN',    label: 'Dentisterie',   icon: 'sentiment_satisfied',countKey: 'count_den_non_hospit',   activiteCode: 'DEN' },
  { codif: 'MAT',    label: 'Maternité',     icon: 'pregnant_woman',     countKey: 'count_mat_non_hospit',   activiteCode: 'MAT' },
  { codif: 'AUX',    label: 'Auxiliaires',   icon: 'support_agent',      countKey: 'count_aux_non_hospit',   activiteCode: 'AUX' },
  { codif: 'EXA',    label: 'Autres exam.',  icon: 'science',            countKey: 'count_exa_non_hospit',   activiteCode: 'EXA' },
  { codif: 'BILN',   label: 'Bilan santé',   icon: 'monitor_heart',      countKey: 'count_biln_non_hospit',  activiteCode: 'BILN' },
  { codif: 'TRA',    label: 'Transport',     icon: 'airport_shuttle',    countKey: 'count_tra_non_hospit',   activiteCode: 'TRA' },
  { codif: 'AMP',    label: 'Procréa',       icon: 'child_friendly',     countKey: 'count_amp_non_hospit',   activiteCode: 'AMP' },
  { codif: 'KINE',   label: 'Kiné',          icon: 'accessibility_new',  countKey: 'count_kine_non_hospit',  activiteCode: 'KINE' },
  { codif: 'HOS',    label: 'Hospitalisation', icon: 'hotel',            countKey: 'count_hos_non_hospit',   activiteCode: 'HOS', hospitOnly: true },
];

@Component({
  selector: 'app-prescription-hub',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, SearchMdicamentProComponent, FormPresOptiqueComponent, FormPresDentisterieComponent],
  templateUrl: './prescription-hub.component.html',
  styleUrl: './prescription-hub.component.scss',
})
export class PrescriptionHubComponent implements OnInit {
  @Input() consultation: any = {};
  @Input() beneficiaire: any = {};
  @Input() hospitId: any = null;
  @Input() showAll: boolean = false;
  @Output() onClose = new EventEmitter<void>();

  user: any = {};
  visibleCols: GarantieCol[] = [];
  itemsGarantie: any[] = [];
  itemsAffection: any[] = [];
  itemsPraticien: any[] = [];

  // ── Modal prescription ──
  showPrescModal = false;
  prescCol: GarantieCol | null = null;
  prescForm: any = {};
  savingPresc = false;

  // actes
  itemsActeModal: any[] = [];
  loadingActes = false;
  selectedActes: any[] = [];
  selectedActeItem: any = {};
  selectedDetailItem: any = null;

  // ordonnance
  itemsMedicament: any[] = [];
  selectedMedocItem: any = {};
  medocForm: any = { quantite: 1, unite_cond: 'boîte(s)' };
  cancelMedocSelected = false;
  defaultMedocItem: any = null;
  selectedPosologie = '';

  // ── Constructeur de posologie ──────────────────────────────────────
  posMode: 'builder' | 'libre' = 'builder';
  posBuilder = { dose: 1, unite: 'cp', freq: '2', matin: true, midi: false, soir: true, coucher: false, repas: 'apres' };
  posTexteLibre = '';

  readonly condUniteOptions = [
    'boîte(s)', 'tube(s)', 'flacon(s)', 'sachet(s)', 'ampoule(s)',
    'plaquette(s)', 'comprimé(s)', 'gélule(s)', 'unité(s)', 'suppositoire(s)',
  ];

  readonly repasOptions = [
    { val: 'apres',    label: 'Après repas' },
    { val: 'avant',    label: 'Avant repas' },
    { val: 'pendant',  label: 'Pendant les repas' },
    { val: 'jeun',     label: 'À jeun' },
    { val: 'sans',     label: 'Indépendant des repas' },
  ];

  readonly freqOptions = [
    { val: '1',         label: '1×/jour' },
    { val: '2',         label: '2×/jour' },
    { val: '3',         label: '3×/jour' },
    { val: '4',         label: '4×/jour' },
    { val: '6h',        label: 'Toutes 6h' },
    { val: '8h',        label: 'Toutes 8h' },
    { val: '12h',       label: 'Toutes 12h' },
    { val: '1s',        label: '1×/semaine' },
    { val: '2s',        label: '2×/semaine' },
    { val: 'si_besoin', label: 'Si besoin' },
  ];

  get uniteOptions(): string[] {
    const f = (this.selectedMedocItem?.forme_medicament_libelle || '').toUpperCase();
    if (f.includes('COMPRIMÉ') || f.includes('COMPRIMES') || f.includes('CPR')) return ['cp', 'comprimé(s)'];
    if (f.includes('GÉLULE') || f.includes('GELULE'))                            return ['gél', 'gélule(s)'];
    if (f.includes('SIROP') || f.includes('SUSPENSION'))                         return ['ml', 'cs', 'cc'];
    if (f.includes('INJECT') || f.includes('AMP') || f.includes('INJ'))          return ['ml', 'ampoule(s)', 'UI'];
    if (f.includes('SACHET'))                                                     return ['sachet(s)'];
    if (f.includes('SUPPOSITOIRE') || f.includes('SUPP'))                        return ['suppositoire(s)'];
    if (f.includes('COLLYRE') || f.includes('GOUTTE'))                           return ['goutte(s)'];
    if (f.includes('POMMADE') || f.includes('CRÈME') || f.includes('GEL'))      return ['application(s)'];
    if (f.includes('PATCH'))                                                      return ['patch(es)'];
    return ['cp', 'gél', 'ml', 'sachet(s)', 'goutte(s)', 'ampoule(s)', 'prise(s)'];
  }

  get isDuplicate(): boolean {
    return !!this.selectedMedocItem?.id &&
      this.itemsMedicament.some(m => m.medicament_id === this.selectedMedocItem.id);
  }

  get posologieFinal(): string {
    return this.posMode === 'builder' ? this.buildPosologieText() : this.posTexteLibre;
  }

  switchMode(mode: 'builder' | 'libre') {
    if (mode === 'libre' && this.posMode === 'builder') {
      this.posTexteLibre = this.buildPosologieText();
    }
    this.posMode = mode;
  }

  buildPosologieText(): string {
    const b = this.posBuilder;
    const dose = `${b.dose} ${b.unite}`.trim();
    const freqLabel: Record<string, string> = {
      '1': '1 fois/jour', '2': '2 fois/jour', '3': '3 fois/jour', '4': '4 fois/jour',
      '6h': 'toutes les 6h', '8h': 'toutes les 8h', '12h': 'toutes les 12h',
      '1s': '1 fois/semaine', '2s': '2 fois/semaine', 'si_besoin': 'si besoin',
    };
    const freq = freqLabel[b.freq] || b.freq;
    const timings: string[] = [];
    if (b.matin)   timings.push('matin');
    if (b.midi)    timings.push('midi');
    if (b.soir)    timings.push('soir');
    if (b.coucher) timings.push('coucher');
    const timingStr = timings.length ? ` (${timings.join(', ')})` : '';
    const repasLabel: Record<string, string> = {
      'avant': 'avant les repas', 'apres': 'après les repas',
      'pendant': 'pendant les repas', 'jeun': 'à jeun', 'sans': '',
    };
    const repasStr = repasLabel[b.repas] ? ` ${repasLabel[b.repas]}` : '';
    const duree = this.medocForm.duree ? ` pendant ${this.medocForm.duree} jours` : '';
    const cond = (this.medocForm.quantite && this.medocForm.unite_cond)
      ? ` — ${this.medocForm.quantite} ${this.medocForm.unite_cond}`
      : '';
    return `${dose} ${freq}${timingStr}${repasStr}${duree}${cond}`.trim();
  }

  private getDefaultCondUnite(): string {
    const c = (this.selectedMedocItem?.conditionnement || '').toUpperCase();
    if (c.includes('BOITE') || c.includes('BOÎTE')) return 'boîte(s)';
    if (c.includes('TUBE'))     return 'tube(s)';
    if (c.includes('FLACON'))   return 'flacon(s)';
    if (c.includes('SACHET'))   return 'sachet(s)';
    if (c.includes('AMPOULE') || c.includes('AMP')) return 'ampoule(s)';
    if (c.includes('PLAQUETTE')) return 'plaquette(s)';
    // Fallback sur la forme galénique
    const f = (this.selectedMedocItem?.forme_medicament_libelle || '').toUpperCase();
    if (f.includes('POMMADE') || f.includes('CRÈME') || f.includes('GEL')) return 'tube(s)';
    if (f.includes('SIROP') || f.includes('SUSPENSION')) return 'flacon(s)';
    if (f.includes('SACHET')) return 'sachet(s)';
    if (f.includes('SUPPOSITOIRE') || f.includes('SUPP')) return 'suppositoire(s)';
    return 'boîte(s)';
  }

  private getDefaultUnite(): string {
    const f = (this.selectedMedocItem?.forme_medicament_libelle || '').toUpperCase();
    if (f.includes('COMPRIMÉ') || f.includes('COMPRIMES') || f.includes('CPR')) return 'cp';
    if (f.includes('GÉLULE') || f.includes('GELULE'))   return 'gél';
    if (f.includes('SIROP') || f.includes('SUSPENSION')) return 'ml';
    if (f.includes('INJECT') || f.includes('AMP') || f.includes('INJ')) return 'ampoule(s)';
    if (f.includes('SACHET'))  return 'sachet(s)';
    if (f.includes('SUPPOSITOIRE') || f.includes('SUPP')) return 'suppositoire(s)';
    if (f.includes('COLLYRE') || f.includes('GOUTTE')) return 'goutte(s)';
    if (f.includes('POMMADE') || f.includes('CRÈME') || f.includes('GEL')) return 'application(s)';
    if (f.includes('PATCH')) return 'patch(es)';
    return 'cp';
  }

  private resetPosBuilder() {
    this.posMode = 'builder';
    this.posBuilder = { dose: 1, unite: 'cp', freq: '2', matin: true, midi: false, soir: true, coucher: false, repas: 'apres' };
    this.posTexteLibre = '';
  }

  constructor(
    private authService: AuthService,
    private restClient: RestClientService,
    private utilities: UtilitiesService,
  ) {
    this.user = this.authService.getCurrentUser();
  }

  ngOnInit() {
    this.buildVisibleCols();
    this.loadGaranties();
    this.loadAffections();
    this.loadPraticiens();
  }

  private readonly ALLOWED_ACTIVITE_CODES = new Set(['ORDONNANCE', 'BIO', 'IMA', 'OPT']);
  private readonly HOSPIT_EXCLUDED_CODES = new Set(['KINE', 'OPT', 'DEN', 'MAT']);

  buildVisibleCols() {
    const userActivites = [
      ...(this.user.activite_utilisateur ?? []),
      ...(this.user.activite_utilisateur_autre ?? []),
    ].map((a: any) => a?.codification?.trim().toLowerCase());
    this.visibleCols = ALL_COLS.filter(c =>
      (this.showAll || this.ALLOWED_ACTIVITE_CODES.has(c.activiteCode)) &&
      (!c.hospitOnly || !!this.hospitId) &&
      (!this.hospitId || !this.HOSPIT_EXCLUDED_CODES.has(c.activiteCode)) &&
      userActivites.includes(c.activiteCode.toLowerCase())
    );
  }

  loadGaranties() {
    this.restClient.post('garantie/getByCriteria', { index: 0, size: 1000, data: {} }).subscribe(
      (res: any) => { if (res?.items) this.itemsGarantie = res.items; }
    );
  }

  loadAffections() {
    this.restClient.post('affection/getByCriteria', { index: 0, size: 1000, data: {} }).subscribe(
      (res: any) => { if (res?.items) this.itemsAffection = res.items; }
    );
  }

  loadPraticiens() {
    this.restClient.post('praticien/searchByPrestataire', { index: 0, size: 1000, data: {} }).subscribe(
      (res: any) => {
        if (res && !res['hasError'] && res['items']) {
          this.itemsPraticien = res.items.map((ip: any) => ({
            ...ip,
            full_name: `${ip.nom} ${ip.prenom} (${ip.specialite_libelle})`
          }));
        } else {
          this.itemsPraticien = [];
        }
      },
      () => { this.itemsPraticien = []; }
    );
  }

  // ── Ouverture ──────────────────────────────────────────────────────

  ouvrirPrescrire(col: GarantieCol) {
    this.prescCol = col;
    this.prescForm = { praticien_id: this.consultation?.praticien_id ?? null };
    this.showPrescModal = true;
    if (col.isOrdonnance) {
      this.itemsMedicament = [];
      this.selectedMedocItem = {};
      this.medocForm = { quantite: 1, unite_cond: 'boîte(s)' };
      this.resetPosBuilder();
      this.cancelMedocSelected = false;
      setTimeout(() => { this.cancelMedocSelected = true; }, 0);
    } else {
      this.selectedActes = [];
      this.selectedActeItem = {};
      this.itemsActeModal = [];
      this.loadActesModal(col);
    }
  }

  fermerPrescModal() {
    this.showPrescModal = false;
    this.prescCol = null;
  }

  annulerSaisie() {
    this.fermerPrescModal();
  }

  // ── Actes ──────────────────────────────────────────────────────────

  loadActesModal(col: GarantieCol) {
    const garantie = this.itemsGarantie.find((g: any) => g.codification === col.codif);
    if (!garantie) return;
    this.loadingActes = true;
    this.restClient.post('acte/getByCriteria', { index: 0, size: 1000, data: { garantie_id: garantie.id } }).subscribe(
      (res: any) => {
        this.loadingActes = false;
        this.itemsActeModal = res?.items?.filter((a: any) => a.is_eligible !== false) ?? [];
      },
      () => { this.loadingActes = false; }
    );
  }

  addActeToCart() {
    if (!this.selectedActeItem?.id) { this.utilities.showSweetNotification('Veuillez sélectionner un acte', 'error'); return; }
    if (this.selectedActes.some(a => a.acte_id === this.selectedActeItem.id)) { this.utilities.showSweetNotification('Acte déjà ajouté', 'error'); return; }
    this.selectedActes.unshift({
      acte_id: this.selectedActeItem.id,
      acte_libelle: this.selectedActeItem.libelle,
      garantie_id: this.selectedActeItem.garantie_id,
      is_entente_prealable: this.selectedActeItem.is_entente_prealable,
      quantite: 1,
    });
    this.selectedActeItem = {};
  }

  removeActe(i: number) { this.selectedActes.splice(i, 1); }

  // ── Ordonnance ─────────────────────────────────────────────────────

  onSelectMedoc(event: any) {
    this.selectedMedocItem = event || {};
    if (this.selectedMedocItem?.id) {
      this.posBuilder.unite = this.getDefaultUnite();
      this.medocForm.unite_cond = this.getDefaultCondUnite();
    } else {
      this.resetPosBuilder();
      this.medocForm = { quantite: 1, unite_cond: 'boîte(s)' };
    }
  }

  addMedocToCart() {
    if (!this.selectedMedocItem?.id) { this.utilities.showSweetNotification('Veuillez sélectionner un médicament', 'error'); return; }
    if (!this.medocForm.quantite) { this.utilities.showSweetNotification('Veuillez indiquer la quantité', 'error'); return; }
    if (!this.medocForm.duree) { this.utilities.showSweetNotification('Veuillez indiquer la durée', 'error'); return; }
    const posologie = this.posologieFinal;
    if (!posologie.trim()) { this.utilities.showSweetNotification('Veuillez renseigner la posologie', 'error'); return; }
    if (this.isDuplicate) { this.utilities.showSweetNotification('Ce médicament est déjà dans l\'ordonnance', 'error'); return; }
    this.itemsMedicament.unshift({
      medicament_id: this.selectedMedocItem.id,
      medicament_libelle: this.selectedMedocItem.libelle,
      forme_medicament_libelle: this.selectedMedocItem.forme_medicament_libelle,
      conditionnement: this.selectedMedocItem.conditionnement || null,
      is_exclu: this.selectedMedocItem.is_exclu,
      is_entente_prealable: this.selectedMedocItem.is_entente_prealable,
      quantite: this.medocForm.quantite,
      unite_cond: this.medocForm.unite_cond || 'boîte(s)',
      posologie,
      duree: this.medocForm.duree,
      is_renewable: 0,
      frequence_renew: null,
      duree_renew: null,
      quantite_renew: null,
      is_consultation_required_renew: 0,
    });
    this.selectedMedocItem = {};
    this.medocForm = { quantite: 1, unite_cond: 'boîte(s)' };
    this.resetPosBuilder();
    this.cancelMedocSelected = false;
    setTimeout(() => { this.cancelMedocSelected = true; }, 0);
  }

  removeMedoc(i: number) { this.itemsMedicament.splice(i, 1); }

  editMedoc(i: number) {
    const m = this.itemsMedicament[i];
    // Reconstruire l'objet médicament pour le composant de recherche
    const medObj = {
      id: m.medicament_id,
      libelle: m.medicament_libelle,
      forme_medicament_libelle: m.forme_medicament_libelle,
      conditionnement: m.conditionnement,
      is_exclu: m.is_exclu,
      is_entente_prealable: m.is_entente_prealable,
    };
    // Retirer du panier
    this.itemsMedicament.splice(i, 1);
    // Pré-remplir les champs
    this.selectedMedocItem = { ...medObj };
    this.defaultMedocItem = { ...medObj };
    this.medocForm = {
      quantite: m.quantite,
      unite_cond: m.unite_cond || 'boîte(s)',
      duree: m.duree,
    };
    // Posologie en mode libre (texte brut sans la partie conditionnement)
    const posBase = (m.posologie || '').replace(/ — \d[\d,.]* .+$/, '').trim();
    this.posMode = 'libre';
    this.posTexteLibre = posBase;
  }

  // ── Sauvegarde ─────────────────────────────────────────────────────

  confirmPrescrire() {
    if (this.prescCol?.isOrdonnance) {
      if (!this.itemsMedicament.length) { this.utilities.showSweetNotification('Veuillez ajouter au moins un médicament', 'error'); return; }

      const renewIncomplete = this.itemsMedicament.filter((im: any) =>
        (im.is_renewable == 1 || im.is_renewable === '1') &&
        (!im.frequence_renew || parseFloat(im.frequence_renew) <= 0 ||
         !im.duree_renew || parseFloat(im.duree_renew) <= 0 ||
         !im.quantite_renew || parseFloat(im.quantite_renew) <= 0)
      );
      if (renewIncomplete.length) {
        this.utilities.showSweetNotification(`Champs de renouvellement incomplets pour : ${renewIncomplete.map((im: any) => im.medicament_libelle).join(', ')}`, 'error');
        return;
      }
      const renewInvalid = this.itemsMedicament.filter((im: any) =>
        (im.is_renewable == 1 || im.is_renewable === '1') &&
        parseFloat(im.duree_renew) <= parseFloat(im.frequence_renew)
      );
      if (renewInvalid.length) {
        this.utilities.showSweetNotification(`Pour : ${renewInvalid.map((im: any) => im.medicament_libelle).join(', ')} — la durée doit être supérieure à la fréquence.`, 'error');
        return;
      }
      const renewQtyExceeded = this.itemsMedicament.filter((im: any) =>
        (im.is_renewable == 1 || im.is_renewable === '1') &&
        parseFloat(im.quantite_renew) > parseFloat(im.quantite)
      );
      if (renewQtyExceeded.length) {
        this.utilities.showSweetNotification(`Pour : ${renewQtyExceeded.map((im: any) => im.medicament_libelle).join(', ')} — la quantité à renouveler ne peut pas dépasser la quantité prescrite.`, 'error');
        return;
      }
    } else {
      if (!this.selectedActes.length) { this.utilities.showSweetNotification('Veuillez ajouter au moins un acte', 'error'); return; }
    }
    Swal.fire({
      title: 'Prescription', text: 'Souhaitez-vous poursuivre?', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#0665aa', cancelButtonColor: '#d33',
      confirmButtonText: 'Oui', cancelButtonText: 'Non',
    }).then(r => { if (r.value) this.savePrescrire(); });
  }

  savePrescrire() {
    const consult = this.consultation;
    const pharmaGarantieId = this.itemsGarantie.find((g: any) => g.codification === 'PHARMA')?.id;
    let datas: any[];

    if (this.prescCol?.isOrdonnance) {
      datas = this.itemsMedicament.map(m => ({
        ...m,
        prestation_acte_id: this.hospitId ? null : consult.id,
        prestation_id: this.hospitId ? null : consult.id,
        beneficiaire_id: consult.beneficiaire_id ?? this.beneficiaire?.id,
        matricule: consult.matricule ?? this.beneficiaire?.matricule,
        affection_id: this.prescForm.affection_id,
        garantie_id: pharmaGarantieId,
        prestataire_id: this.user.prestataire_id,
        praticien_id: this.prescForm.praticien_id,
        dossier_id: consult.dossier_id,
        libelle: m.medicament_libelle,
        is_factured: false,
      }));
    } else {
      datas = this.selectedActes.map(a => ({
        ...a,
        prestation_acte_id: this.hospitId ? null : consult.id,
        beneficiaire_id: consult.beneficiaire_id ?? this.beneficiaire?.id,
        matricule: consult.matricule ?? this.beneficiaire?.matricule,
        affection_id: this.prescForm.affection_id,
        renseignement_clinique: this.prescForm.renseignement_clinique,
        prestataire_id: this.user.prestataire_id,
        praticien_id: this.prescForm.praticien_id,
        dossier_id: consult.dossier_id,
      }));
    }

    datas.forEach((d: any) => {
      d.prestataire_id = this.user.prestataire_id;
      if (this.hospitId) {
        d.is_hospit = 1;
        d.hospit_id = this.hospitId;
      }
      if (d.is_renewable == 1 || d.is_renewable === '1') {
        const frequence = parseFloat(d.frequence_renew) || 0;
        const duree = parseFloat(d.duree_renew) || 0;
        d.total_renew_done = 0;
        d.total_renew_possible = frequence > 0 ? Math.floor(duree / frequence) : 0;
        d.total_renew_remaining = d.total_renew_possible;
        if (frequence > 0) {
          const baseDate = new Date();
          const nextDate = new Date(baseDate);
          nextDate.setDate(baseDate.getDate() + frequence);
          d.next_renew_date = nextDate.toISOString().split('T')[0];
          d.renouvellements = Array.from({ length: d.total_renew_possible }, (_, k) => {
            const dt = new Date(baseDate);
            dt.setDate(baseDate.getDate() + frequence * (k + 1));
            return dt.toISOString().split('T')[0];
          });
        }
      }
    });
    this.savingPresc = true;
    this.restClient.post('prescriptionActe/create', { datas }).subscribe(
      (res: any) => {
        this.savingPresc = false;
        if (res && !res['hasError']) {
          this.utilities.showSweetNotification(res['status']['message'], 'succes');
          this.fermerPrescModal();
        } else {
          this.utilities.showSweetNotification(res['status']['message'], 'error');
        }
      },
      (err: any) => { this.savingPresc = false; this.utilities.showSweetNotification(err, 'error'); }
    );
  }

  get renewalDates(): string[] {
    const frequence = parseFloat(this.selectedDetailItem?.frequence_renew);
    const duree = parseFloat(this.selectedDetailItem?.duree_renew);
    if (!frequence || !duree || duree <= frequence) return [];
    const count = Math.floor(duree / frequence);
    const base = new Date();
    return Array.from({ length: count }, (_, k) => {
      const d = new Date(base);
      d.setDate(base.getDate() + frequence * (k + 1));
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    });
  }

  rowClass(m: any): string {
    if (m.is_exclu) return 'table-danger';
    if (m.is_entente_prealable) return 'table-warning';
    return 'table-success';
  }
}
